import { PriceType, ResolvedPrice } from 'types/api/pricing.type';

import { parsePrice } from 'helpers/parsePrice';

/**
 * Extracts the resolved price entry for the given price type.
 * Falls back to the default price if the requested type is not found.
 */
const getResolvedPriceByType = (
  resolvedPrices: ResolvedPrice[] | undefined,
  selectedType: PriceType | undefined
): ResolvedPrice | undefined => {
  if (!resolvedPrices || resolvedPrices.length === 0) return undefined;

  if (selectedType) {
    const match = resolvedPrices.find((rp) => rp.type === selectedType);
    if (match) return match;
  }

  // Fallback: use the entry marked as default
  return resolvedPrices.find((rp) => rp.isDefault) ?? resolvedPrices[0];
};

/**
 * Returns formatted price string (e.g. "EUR 35.00") or a dash
 * when the price is not available.
 */
const formatResolvedPrice = (resolved: ResolvedPrice | undefined): string => {
  if (!resolved || resolved.status !== 'FOUND' || !resolved.price) {
    return '—';
  }

  const value = parsePrice(resolved.price.price);
  return `${resolved.price.currency} ${value.toFixed(2)}`;
};

/**
 * Returns the default PriceType from a resolvedPrices array.
 */
const getDefaultPriceType = (
  resolvedPrices: ResolvedPrice[] | undefined
): PriceType | undefined => {
  if (!resolvedPrices || resolvedPrices.length === 0) return undefined;
  const defaultEntry = resolvedPrices.find((rp) => rp.isDefault);
  return defaultEntry?.type ?? resolvedPrices[0]?.type;
};

/**
 * The price type a selector should start on: the user's remembered choice when
 * this product/order offers it, otherwise the default type.
 */
const getInitialPriceType = (
  remembered: PriceType | undefined,
  availableTypes: PriceType[],
  defaultType: PriceType | undefined
): PriceType | undefined =>
  remembered && availableTypes.includes(remembered) ? remembered : defaultType;

/**
 * Returns all available (distinct) price types from a list of resolvedPrices arrays.
 * Useful to build a price type selector from all sizes' resolved prices.
 */
const getAvailablePriceTypes = (
  allResolvedPrices: (ResolvedPrice[] | undefined)[]
): PriceType[] => {
  const typeSet = new Set<PriceType>();

  allResolvedPrices.forEach((resolvedPrices) => {
    resolvedPrices?.forEach((rp) => typeSet.add(rp.type));
  });

  // Maintain logical order
  const order: PriceType[] = ['DISTRIBUTOR', 'WHOLESALE', 'RETAIL'];
  return order.filter((t) => typeSet.has(t));
};

/**
 * The price types any size of an order resolves — what the order page's price
 * selector and the export price-type modal offer for that order.
 */
const getOrderAvailablePriceTypes = (
  items: { sizes?: { resolvedPrices?: ResolvedPrice[] }[] }[]
): PriceType[] =>
  getAvailablePriceTypes(
    items.flatMap((item) => (item.sizes ?? []).map((size) => size.resolvedPrices))
  );

export {
  formatResolvedPrice,
  getAvailablePriceTypes,
  getDefaultPriceType,
  getInitialPriceType,
  getOrderAvailablePriceTypes,
  getResolvedPriceByType,
};
